import React, { useState, useEffect } from 'react';
import { 
  Brain, Loader2, CheckCircle, AlertCircle, ChevronRight, ChevronLeft
} from 'lucide-react';
import { studentPanelAPI } from '../../services/api';

const TEMPERAMENT_INFO = {
  choleric: {
    name: 'Xolerik',
    color: '#ef4444',
    description: 'Faol, tashabbuskor, qat\'iyatli. Tez qaror qabul qiladi, liderlik xususiyatlariga ega.',
    traits: ['Tashabbuskor', 'Qat\'iyatli', 'Tez jahli chiqadi', 'Faol', 'Lider']
  },
  sanguine: {
    name: 'Sangvinik',
    color: '#f59e0b',
    description: 'Quvnoq, muomalaga kirishimli, optimist. Yangi muhitga tez moslashadi.',
    traits: ['Quvnoq', 'Muloqotchan', 'Optimist', 'Moslashuvchan', 'Ijtimoiy']
  },
  phlegmatic: {
    name: 'Flegmatik',
    color: '#10b981',
    description: 'Tinch, vazmin, sabr-toqatli. Ishda izchil va ishonchli.',
    traits: ['Tinch', 'Sabr-toqatli', 'Izchil', 'Ishonchli', 'Vazmin']
  },
  melancholic: {
    name: 'Melanxolik',
    color: '#6366f1',
    description: 'Sezgir, chuqur fikrlovchi, ijodkor. Batafsil va ehtiyotkorlik bilan ish tutadi.',
    traits: ['Sezgir', 'Chuqur fikrli', 'Ijodkor', 'Ehtiyotkor', 'Tahlilchi']
  }
};

const StudentTemperament = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const questionsPerPage = 10;

  useEffect(() => {
    fetchTest();
  }, []);

  const fetchTest = async () => {
    try {
      const response = await studentPanelAPI.getTemperament();
      if (response.data.completed) {
        setCompleted(true);
        setResult(response.data.result);
      } else {
        setQuestions(response.data.questions || []);
      }
    } catch (error) {
      console.error('Test yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      alert(`Barcha savollarga javob bering! (${Object.keys(answers).length}/${questions.length})`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await studentPanelAPI.submitTemperament(answers);
      setResult(response.data.result);
      setCompleted(true);
    } catch (error) {
      console.error('Yuborishda xato:', error);
      alert(error.response?.data?.error || 'Xatolik yuz berdi');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  // Natija ko'rsatish
  if (completed && result) {
    const tempInfo = TEMPERAMENT_INFO[result.dominant_type];
    
    return (
      <div className="page-container">
        <header className="page-header">
          <div className="page-title">
            <h1><Brain size={28} /> Temperament test natijasi</h1>
            <p>Sizning xarakter tipingiz aniqlandi</p>
          </div>
        </header>

        <div className="temperament-result-container">
          {/* Asosiy natija */}
          <div className="result-main-card" style={{ '--temp-color': tempInfo.color }}>
            <div className="result-badge">
              <CheckCircle size={48} />
            </div>
            <h2>Sizning temperamentingiz:</h2>
            <div className="result-type">{tempInfo.name}</div>
            <p className="result-description">{tempInfo.description}</p>
            <div className="result-traits">
              {tempInfo.traits.map((trait, idx) => (
                <span key={idx} className="trait-badge">{trait}</span>
              ))}
            </div>
          </div>

          {/* Ballar */}
          <div className="result-scores">
            <h3>Barcha temperamentlar bo'yicha ballar:</h3>
            <div className="scores-grid">
              {Object.entries(TEMPERAMENT_INFO).map(([key, info]) => {
                const score = result[`${key}_score`];
                const percentage = (score / 20) * 100;
                const isMain = key === result.dominant_type;
                
                return (
                  <div key={key} className={`score-item ${isMain ? 'main' : ''}`}>
                    <div className="score-header">
                      <span className="score-name" style={{ color: info.color }}>{info.name}</span>
                      <span className="score-value">{score}/20</span>
                    </div>
                    <div className="score-bar">
                      <div 
                        className="score-fill" 
                        style={{ width: `${percentage}%`, background: info.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test sahifasi
  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Brain size={28} /> Temperament testi</h1>
          <p>80 ta savoldan iborat psixologik test</p>
        </div>
      </header>

      {/* Progress */}
      <div className="test-progress-card">
        <div className="progress-info">
          <span>Jarayon: {answeredCount} / {questions.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Ko'rsatma */}
      <div className="test-instruction">
        <AlertCircle size={20} />
        <p>Har bir savolni o'qing va sizga mos kelsa <strong>"Ha"</strong>, mos kelmasa <strong>"Yo'q"</strong> tugmasini bosing.</p>
      </div>

      {/* Savollar */}
      <div className="questions-container">
        {currentQuestions.map((question, idx) => {
          const globalIdx = currentPage * questionsPerPage + idx + 1;
          const isAnswered = answers[question.id] !== undefined;
          
          return (
            <div key={question.id} className={`question-card ${isAnswered ? 'answered' : ''}`}>
              <div className="question-number">{globalIdx}</div>
              <div className="question-content">
                <p>{question.question_text}</p>
                <div className="question-actions">
                  <button 
                    className={`answer-btn yes ${answers[question.id] === true ? 'selected' : ''}`}
                    onClick={() => handleAnswer(question.id, true)}
                  >
                    Ha
                  </button>
                  <button 
                    className={`answer-btn no ${answers[question.id] === false ? 'selected' : ''}`}
                    onClick={() => handleAnswer(question.id, false)}
                  >
                    Yo'q
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="test-pagination">
        <button 
          className="btn btn-secondary"
          onClick={() => setCurrentPage(p => p - 1)}
          disabled={currentPage === 0}
        >
          <ChevronLeft size={18} />
          Oldingi
        </button>
        
        <div className="page-numbers">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`page-num ${currentPage === i ? 'active' : ''}`}
              onClick={() => setCurrentPage(i)}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {currentPage < totalPages - 1 ? (
          <button 
            className="btn btn-primary"
            onClick={() => setCurrentPage(p => p + 1)}
          >
            Keyingi
            <ChevronRight size={18} />
          </button>
        ) : (
          <button 
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={submitting || answeredCount !== questions.length}
          >
            {submitting ? <Loader2 className="spinner" size={18} /> : <CheckCircle size={18} />}
            Yakunlash
          </button>
        )}
      </div>
    </div>
  );
};

export default StudentTemperament;
